import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Phone, MapPin, Clock, Star, Heart, Shield, Award, Users, Calendar } from 'lucide-react'
import { Header } from '@/components/Header'
import { Footer } from '@/components/Footer'
import { SEO } from '@/components/SEO'
import { ClinicVideo } from '@/components/ClinicVideo'
import drAishwaryaImage from '@/assets/aishwarya_1.jpg'
import '@/styles/animations.css'

const services = [
  {
    title: 'Preventive Care',
    description: 'Regular checkups and cleanings to keep your teeth healthy',
    icon: Shield
  },
  {
    title: 'Cosmetic Dentistry',
    description: 'Teeth whitening, veneers, and smile makeovers',
    icon: Heart
  },
  {
    title: 'Restorative Care',
    description: 'Fillings, crowns, bridges, and dental implants',
    icon: Award
  },
  {
    title: 'Orthodontics',
    description: 'Braces and aligners for straighter teeth',
    icon: Users
  }
]

const testimonials = [
  {
    name: 'Varun Kumar',
    review: "I've been under the care of Dr. Aishwarya at SRI SAI SABARI DENTAL CLINIC for the past two years, and I can't express how satisfied I am with the treatment.",
    rating: 5
  },
  {
    name: 'Uthra Bharathi',
    review: "From my childhood I'm so scared of dentist but Dr. Aishwarya made me feel so comfortable. She's very gentle and explains everything clearly.",
    rating: 5
  },
  {
    name: 'Sathiya Venkatesan',
    review: "I done a root canal treatment here and had a very nice experience. Dr. Aishwarya is doing a great job.",
    rating: 5
  },
  {
    name: 'Bragadeesh S',
    review: "I was extremely nervous about having my tooth treated, but Dr. Aishwarya was able to ease my nerves and make the experience comfortable.",
    rating: 5
  }
]

export function HomePage() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "DentalClinic",
    "name": "Sri Sai Sabari Dental Clinic",
    "description": "Professional dental care in Chennai by Dr. Aishwarya V. Comprehensive dental services including general dentistry, cosmetic procedures, orthodontics, and emergency care.",
    "url": "https://sssdental.com",
    "telephone": ["+91-9841817510", "+91-6381823239"],
    "email": "smile@sssdental.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "East Main Road, opposite to leobags, Puzhuthivakkam",
      "addressLocality": "Madipakkam",
      "addressRegion": "Tamil Nadu",
      "postalCode": "600091",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "12.9171",
      "longitude": "80.2707"
    },
    "openingHours": "Mo,Tu,We,Th,Fr,Sa,Su 09:00-20:30",
    "priceRange": "$$",
    "paymentAccepted": "Cash, Credit Card, Debit Card",
    "currenciesAccepted": "INR",
    "hasMap": "https://maps.app.goo.gl/QDpBdRjGZW2eKb2m7",
    "foundingDate": "2019",
    "founder": {
      "@type": "Person",
      "name": "Dr. Aishwarya V",
      "jobTitle": "Dentist",
      "description": "BDS, FAAD (Germany) - Experienced dental professional specializing in comprehensive dental care"
    },
    "medicalSpecialty": ["General Dentistry", "Cosmetic Dentistry", "Orthodontics", "Endodontics", "Periodontics", "Pediatric Dentistry", "Oral Surgery"],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "50",
      "bestRating": "5"
    }
  }

  return (
    <div className="min-h-screen">
      <SEO 
        title="Sri Sai Sabari Dental Clinic - Best Dentist in Chennai | Dr. Aishwarya V"
        description="Professional dental care in Chennai by Dr. Aishwarya V. Comprehensive dental services including general dentistry, cosmetic procedures, orthodontics, and emergency care. Book your appointment today!"
        keywords="best dentist Chennai, dental clinic Madipakkam, Dr. Aishwarya V dentist, dental care Chennai, teeth cleaning, root canal treatment, dental implants, orthodontics Chennai, cosmetic dentistry, emergency dental care"
        canonical="/"
        structuredData={structuredData}
      />
      <Header currentPage="home" />

      {/* Hero Section */}
      <section id="home" className="bg-gradient-to-br from-black via-gray-900 to-black py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/10 via-yellow-500/8 to-yellow-600/5 animate-pulse"></div>
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-20 h-20 bg-gradient-to-br from-yellow-400/20 to-yellow-600/15 rounded-full blur-xl animate-bounce" style={{animationDelay: '0s'}}></div>
          <div className="absolute top-1/2 right-20 w-16 h-16 bg-gradient-to-br from-yellow-500/15 to-yellow-700/10 rounded-full blur-lg animate-bounce" style={{animationDelay: '2s'}}></div>
          <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-gradient-to-br from-yellow-300/10 to-yellow-500/8 rounded-full blur-md animate-bounce" style={{animationDelay: '4s'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up" style={{animationDelay: '0.2s'}}>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 hover:scale-105 transition-transform duration-300">
                Your Smile is Our 
                <span className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent animate-pulse"> Priority</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 animate-fade-in" style={{animationDelay: '0.5s', animationFillMode: 'both'}}>
                Experience gentle, professional dental care with Dr. Aishwarya V at Chennai's trusted dental clinic. We provide comprehensive dental services in a comfortable, modern environment.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in" style={{animationDelay: '0.8s', animationFillMode: 'both'}}>
                <Button size="lg" asChild className="bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 hover:from-yellow-600 hover:via-yellow-700 hover:to-yellow-800 text-white font-semibold transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl hover:shadow-yellow-500/50">
                  <Link to="/book-appointment">
                    <Calendar className="mr-2 h-5 w-5" />
                    Book Appointment
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="border-2 border-yellow-500 text-yellow-600 hover:bg-gradient-to-r hover:from-yellow-500 hover:via-yellow-600 hover:to-yellow-700 hover:text-white hover:border-yellow-600 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl hover:shadow-yellow-400/30">
                  <a href="tel:+91-9841817510">
                    <Phone className="mr-2 h-5 w-5" />
                    Call Now
                  </a>
                </Button>
              </div>
            </div>
            <div className="relative animate-fade-in-right" style={{animationDelay: '0.4s', animationFillMode: 'both'}}>
              <div className="bg-white rounded-2xl shadow-2xl p-8 border-2 border-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 transform hover:scale-105 transition-all duration-500 hover:shadow-3xl hover:shadow-yellow-400/20">
                <div className="text-center mb-6">
                  <div className="w-32 h-32 mx-auto mb-4 relative group">
                    <img 
                      src={drAishwaryaImage} 
                      alt="Dr. Aishwarya V - Dental Specialist at Sri Sai Sabari Dental Clinic"
                      className="w-full h-full object-cover rounded-full shadow-lg border-4 border-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 transform group-hover:scale-110 transition-all duration-300 hover:shadow-2xl"
                    />
                    <div className="absolute inset-0 rounded-full border-4 border-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 animate-ping opacity-20"></div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 hover:bg-gradient-to-r hover:from-yellow-600 hover:via-yellow-700 hover:to-yellow-800 hover:bg-clip-text hover:text-transparent transition-all duration-300">Dr. Aishwarya V</h3>
                  <p className="text-gray-600 hover:text-gray-800 transition-colors duration-300">BDS, FAAD (Germany)</p>
                  <p className="text-sm text-gray-500 mt-1">TN Dental Council Reg: 26411</p>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center text-sm text-gray-600 hover:bg-gradient-to-r hover:from-yellow-600 hover:via-yellow-700 hover:to-yellow-800 hover:bg-clip-text hover:text-transparent transition-all duration-300 group">
                    <MapPin className="h-4 w-4 mr-2 text-yellow-600 group-hover:animate-bounce" />
                    <span>Puzhuthivakkam, Chennai</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600 hover:bg-gradient-to-r hover:from-yellow-600 hover:via-yellow-700 hover:to-yellow-800 hover:bg-clip-text hover:text-transparent transition-all duration-300 group">
                    <Calendar className="h-4 w-4 mr-2 text-yellow-600 group-hover:animate-bounce" />
                    <span>Open All Days - Because your smile can't wait</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600 hover:bg-gradient-to-r hover:from-yellow-600 hover:via-yellow-700 hover:to-yellow-800 hover:bg-clip-text hover:text-transparent transition-all duration-300 group">
                    <Clock className="h-4 w-4 mr-2 text-yellow-600 group-hover:animate-spin" />
                    <span>9 AM - 8:30 PM</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600 hover:bg-gradient-to-r hover:from-yellow-600 hover:via-yellow-700 hover:to-yellow-800 hover:bg-clip-text hover:text-transparent transition-all duration-300 group">
                    <Star className="h-4 w-4 mr-2 text-yellow-500 group-hover:animate-pulse" />
                    <span>4.9/5 Rating (50+ Reviews)</span>
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full animate-ping opacity-30"></div>
              <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-gradient-to-br from-yellow-500 to-yellow-700 rounded-full animate-bounce opacity-40"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Highlights */}
      <section className="py-16 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-10 w-32 h-32 bg-gradient-to-br from-yellow-300/10 via-yellow-400/8 to-yellow-500/6 rounded-full blur-2xl animate-float" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-1/4 right-10 w-24 h-24 bg-gradient-to-br from-yellow-400/15 via-yellow-500/12 to-yellow-600/10 rounded-full blur-xl animate-float" style={{animationDelay: '3s'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 animate-fade-in-up" style={{animationDelay: '0.2s', animationFillMode: 'both'}}>Our Services</h2>
            <p className="text-xl text-gray-600 animate-fade-in-up" style={{animationDelay: '0.4s', animationFillMode: 'both'}}>Comprehensive dental care for the whole family</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon
              return (
                <div key={index} className={`text-center p-6 rounded-lg bg-white border-2 border-gradient-to-r from-yellow-200 via-yellow-300 to-yellow-400 hover:shadow-2xl hover:border-gradient-to-r hover:from-yellow-400 hover:via-yellow-500 hover:to-yellow-600 transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 group animate-fade-in-up hover-lift cursor-pointer`} style={{animationDelay: `${0.6 + index * 0.2}s`, animationFillMode: 'both'}}>
                  <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 rounded-full mx-auto mb-4 flex items-center justify-center group-hover:animate-bounce shadow-lg group-hover:shadow-xl transition-all duration-300">
                    <Icon className="h-8 w-8 text-white group-hover:text-gray-100 transition-colors duration-300 group-hover:scale-110" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:bg-gradient-to-r group-hover:from-yellow-600 group-hover:via-yellow-700 group-hover:to-yellow-800 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">{service.title}</h3>
                  <p className="text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{service.description}</p>
                  <div className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="h-1 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                  </div>
                </div>
              )
            })}
          </div>
          <div className="text-center mt-12">
            <Button asChild variant="outline" size="lg" className="border-2 border-yellow-500 text-yellow-600 hover:bg-gradient-to-r hover:from-yellow-500 hover:via-yellow-600 hover:to-yellow-700 hover:text-white hover:border-yellow-600 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl hover:shadow-yellow-500/30 animate-fade-in" style={{animationDelay: '1.4s', animationFillMode: 'both'}}>
              <Link to="/services">View All Services</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Clinic Video */}
      <ClinicVideo />

      {/* Testimonials */}
      <section className="py-16 bg-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 right-1/4 w-40 h-40 bg-gradient-to-br from-yellow-200/10 via-yellow-300/8 to-yellow-400/6 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
          <div className="absolute bottom-1/3 left-1/4 w-28 h-28 bg-gradient-to-br from-yellow-300/15 via-yellow-400/12 to-yellow-500/10 rounded-full blur-2xl animate-float" style={{animationDelay: '4s'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 animate-fade-in-up" style={{animationDelay: '0.2s', animationFillMode: 'both'}}>What Our Patients Say</h2>
            <p className="text-xl text-gray-600 animate-fade-in-up" style={{animationDelay: '0.4s', animationFillMode: 'both'}}>Real experiences from our valued patients</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className={`bg-gradient-to-br from-gray-50 to-white p-6 rounded-lg shadow-md border-l-4 border-gradient-to-b from-yellow-400 via-yellow-500 to-yellow-600 hover:shadow-2xl transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 group animate-fade-in-up cursor-pointer hover-lift`} style={{animationDelay: `${0.6 + index * 0.15}s`, animationFillMode: 'both'}}>
                <div className="flex items-center mb-4">
                  {Array.from({ length: testimonial.rating }).map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 fill-current group-hover:animate-pulse transition-all duration-300" style={{animationDelay: `${i * 0.1}s`}} />
                  ))}
                </div>
                <p className="text-gray-600 mb-4 line-clamp-3 italic group-hover:text-gray-700 transition-colors duration-300">{testimonial.review}</p>
                <p className="font-semibold text-gray-900 group-hover:bg-gradient-to-r group-hover:from-yellow-600 group-hover:via-yellow-700 group-hover:to-yellow-800 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">{testimonial.name}</p>
                <div className="absolute top-2 right-2 w-2 h-2 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full opacity-0 group-hover:opacity-100 animate-ping transition-opacity duration-300"></div>
                <div className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 rounded-full"></div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button asChild variant="outline" size="lg" className="border-2 border-yellow-500 text-yellow-600 hover:bg-gradient-to-r hover:from-yellow-500 hover:via-yellow-600 hover:to-yellow-700 hover:text-white hover:border-yellow-600 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl hover:shadow-yellow-500/30 animate-fade-in" style={{animationDelay: '1.2s', animationFillMode: 'both'}}>
              <Link to="/testimonials">Read All Reviews</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Location Preview */}
      <section className="py-16 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/3 w-36 h-36 bg-gradient-to-br from-yellow-300/10 via-yellow-400/8 to-yellow-500/6 rounded-full blur-2xl animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-1/4 right-1/3 w-20 h-20 bg-gradient-to-br from-yellow-400/15 via-yellow-500/12 to-yellow-600/10 rounded-full blur-xl animate-float" style={{animationDelay: '3s'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-left" style={{animationDelay: '0.2s', animationFillMode: 'both'}}>
              <h2 className="text-3xl font-bold text-gray-900 mb-6 hover:bg-gradient-to-r hover:from-yellow-600 hover:via-yellow-700 hover:to-yellow-800 hover:bg-clip-text hover:text-transparent transition-all duration-300">Visit Our Clinic</h2>
              <div className="space-y-4">
                <div className="flex items-start group hover:bg-white hover:p-4 hover:rounded-lg hover:shadow-lg transition-all duration-300 animate-fade-in-up" style={{animationDelay: '0.4s', animationFillMode: 'both'}}>
                  <MapPin className="h-6 w-6 bg-gradient-to-br from-yellow-600 via-yellow-700 to-yellow-800 text-white rounded p-1 mr-3 mt-1 group-hover:animate-bounce" />
                  <div>
                    <p className="text-gray-900 font-medium group-hover:bg-gradient-to-r group-hover:from-yellow-600 group-hover:via-yellow-700 group-hover:to-yellow-800 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">Sri Sai Sabari Dental Clinic</p>
                    <p className="text-gray-600 group-hover:text-gray-700 transition-colors duration-300">East Main Road, opposite to leobags, Puzhuthivakkam, Madipakkam, Chennai, Tamil Nadu 600091</p>
                  </div>
                </div>
                <div className="flex items-center group hover:bg-white hover:p-4 hover:rounded-lg hover:shadow-lg transition-all duration-300 animate-fade-in-up" style={{animationDelay: '0.6s', animationFillMode: 'both'}}>
                  <Phone className="h-6 w-6 bg-gradient-to-br from-yellow-600 via-yellow-700 to-yellow-800 text-white rounded p-1 mr-3 group-hover:animate-pulse" />
                  <div>
                    <p className="text-gray-900 font-medium group-hover:bg-gradient-to-r group-hover:from-yellow-600 group-hover:via-yellow-700 group-hover:to-yellow-800 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">+91-9841817510</p>
                    <p className="text-gray-600 group-hover:text-gray-700 transition-colors duration-300">Call for appointments</p>
                  </div>
                </div>
                <div className="flex items-center group hover:bg-white hover:p-4 hover:rounded-lg hover:shadow-lg transition-all duration-300 animate-fade-in-up" style={{animationDelay: '0.8s', animationFillMode: 'both'}}>
                  <Clock className="h-6 w-6 bg-gradient-to-br from-yellow-600 via-yellow-700 to-yellow-800 text-white rounded p-1 mr-3 group-hover:animate-spin" />
                  <div>
                    <p className="text-gray-900 font-medium group-hover:bg-gradient-to-r group-hover:from-yellow-600 group-hover:via-yellow-700 group-hover:to-yellow-800 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">Open All Days - Because your smile can't wait</p>
                    <p className="text-gray-600 group-hover:text-gray-700 transition-colors duration-300">9:00 AM - 8:30 PM</p>
                  </div>
                </div>
              </div>
              <div className="mt-8 animate-fade-in" style={{animationDelay: '1.0s', animationFillMode: 'both'}}>
                <Button asChild className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl hover:shadow-yellow-500/50">
                  <a href="https://g.co/kgs/swqYX7W" target="_blank" rel="noopener noreferrer">
                    <MapPin className="mr-2 h-5 w-5" />
                    View on Google Maps
                  </a>
                </Button>
              </div>
            </div>
            <div className="relative animate-fade-in-right" style={{animationDelay: '0.4s', animationFillMode: 'both'}}>
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-8 border border-gray-200 hover:shadow-2xl hover:border-yellow-600 transition-all duration-500 transform hover:scale-105 group">
                <div className="text-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 rounded-full mx-auto mb-6 flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300">
                    <MapPin className="h-12 w-12 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-yellow-700 transition-colors duration-300">Find Our Location</h3>
                  <p className="text-gray-600 mb-6 group-hover:text-gray-700 transition-colors duration-300">
                    Get directions to our clinic with just one click. We're conveniently located in Puzhuthivakkam, Chennai.
                  </p>
                  <Button asChild size="lg" className="bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 hover:from-yellow-600 hover:via-yellow-700 hover:to-yellow-800 text-white font-semibold transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl w-full">
                    <a href="https://maps.app.goo.gl/QDpBdRjGZW2eKb2m7" target="_blank" rel="noopener noreferrer">
                      <MapPin className="mr-2 h-5 w-5" />
                      Open in Google Maps
                    </a>
                  </Button>
                </div>
              </div>
              <div className="absolute -top-2 -right-2 w-4 h-4 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full animate-ping opacity-50"></div>
              <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-gradient-to-br from-yellow-500 to-yellow-700 rounded-full animate-bounce opacity-60"></div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
